import { useState } from 'react'
import type { ReactNode } from 'react'
import classNames from 'classnames'
import { Filter, Search, X } from 'lucide-react'
import Button from '@/components/button'
import Spinner from '@/components/spinner'
import styles from './style.module.css'

export interface FilterChip {
  key: string
  label: string
  value: string
  onRemove: () => void
}

interface FilterBarProps {
  searchValue: string
  onSearchChange: (valor: string) => void
  onSearchClear: () => void
  searchPlaceholder: string
  searchLabel: string
  isSearching?: boolean
  quickFilters?: ReactNode
  quickFiltersLabel?: string
  advanced: ReactNode
  chips: FilterChip[]
  activeCount: number
  onApply: () => void
  onClear: () => void
}

const FilterBar = ({
  activeCount,
  advanced,
  chips,
  isSearching = false,
  onApply,
  onClear,
  onSearchChange,
  onSearchClear,
  quickFilters,
  quickFiltersLabel = 'Situação',
  searchLabel,
  searchPlaceholder,
  searchValue,
}: FilterBarProps) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false)

  const aplicar = () => {
    setIsPanelOpen(false)
    onApply()
  }

  const limpar = () => {
    setIsPanelOpen(false)
    onClear()
  }

  return (
    <section className={styles.wrapper}>
      <div className={styles.toolbar}>
        <div className={styles.search}>
          <Search className={styles.searchIcon} size={16} aria-hidden />
          <input
            type="text"
            className={styles.searchInput}
            placeholder={searchPlaceholder}
            aria-label={searchLabel}
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
          />
          {isSearching ? (
            <span className={styles.searchSpinner}>
              <Spinner size={14} label="Buscando" />
            </span>
          ) : null}
          {searchValue && !isSearching ? (
            <button
              type="button"
              className={styles.searchClear}
              onClick={onSearchClear}
              aria-label="Limpar busca"
            >
              <X size={14} />
            </button>
          ) : null}
        </div>

        <button
          type="button"
          className={classNames(styles.funnel, {
            [styles.funnelAtivo]: activeCount > 0 || isPanelOpen,
          })}
          onClick={() => setIsPanelOpen((aberto) => !aberto)}
          aria-expanded={isPanelOpen}
          aria-label="Filtros"
        >
          <Filter size={16} />
          {activeCount > 0 ? <span className={styles.count}>{activeCount}</span> : null}
        </button>
      </div>

      {isPanelOpen ? (
        <div className={styles.panel}>
          <div className={styles.panelFields}>{advanced}</div>

          {quickFilters ? (
            <div className={styles.quickFilters}>
              <span className={styles.quickFiltersLabel}>{quickFiltersLabel}</span>
              {quickFilters}
            </div>
          ) : null}

          <div className={styles.panelActions}>
            <Button variant="ghost" onClick={limpar}>
              Limpar
            </Button>
            <Button onClick={aplicar}>Aplicar filtros</Button>
          </div>
        </div>
      ) : null}

      {chips.length > 0 ? (
        <div className={styles.chips}>
          {chips.map((chip) => (
            <span className={styles.chip} key={chip.key}>
              <span className={styles.chipText}>
                {chip.label}: {chip.value}
              </span>
              <button
                type="button"
                className={styles.chipRemove}
                onClick={chip.onRemove}
                aria-label="Remover filtro"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </section>
  )
}

export default FilterBar
